<template>
  <component
    :is="config.isList ? 'fieldset' : 'div'"
    class="field-renderer"
    :class="{ ['field-renderer--' + fieldName]: true, 'field-renderer--list': config.isList }"
    :style="style"
    v-auto-animate="{ disabled: !config.isList }"
  >
    <button
      v-if="config.isList"
      class="field-renderer__list-header"
      @click="isCollapsed = !isCollapsed"
      type="button"
    >
      <FontAwesomeIcon
        :icon="['fal', 'angle-down']"
        size="2x"
        :rotation="isCollapsed ? '0' : '180'"
      />
      <legend class="field-renderer__list-labels">
        <div class="field-renderer__list-labels-label">{{ schema.label }}</div>
        <div v-if="config.binding.sublabel" class="field-renderer__list-labels-sublabel">
          {{ config.binding.sublabel }}
        </div>
      </legend>
      <span class="fade" style="margin-left: auto">
        {{ data.length }} {{ $tc('field', data.length).toLowerCase() }}
      </span>
    </button>
    <template v-if="!config.isList || (config.isList && !isCollapsed)">
      <div v-for="(item, index) in data" :key="getId(index)" class="field-renderer__item">
        <component
          :is="config.component"
          class="custom-field"
          :class="['custom-field--' + fieldName]"
          v-bind="getBinding(index)"
          @input="$ev => set($ev, index)"
        />
        <div
          v-if="config.removable || (config.isList && data.length > 1)"
          class="field-renderer__actions"
        >
          <ButtonComponent
            v-if="data.length > 1 || config.removable"
            v-tooltip="$t('remove')"
            :icon="['fal', 'trash']"
            class="field-renderer__action-remove"
            type="button"
            @click="remove(index)"
          />
          <template v-if="config.isList && data.length > 1">
            <ButtonComponent
              :icon="['fal', 'arrow-up']"
              v-tooltip="$t('moveUp')"
              class="field-renderer__action-up"
              type="button"
              :disabled="index === 0"
              @click="move(index, -1)"
            />
            <ButtonComponent
              :icon="['fal', 'arrow-down']"
              v-tooltip="$t('moveDown')"
              class="field-renderer__action-down"
              type="button"
              :disabled="index === data.length - 1"
              @click="move(index, 1)"
            />
          </template>
        </div>
      </div>
      <ButtonComponent
        v-if="config.isList"
        :label="$t('add')"
        :icon="['fal', 'plus']"
        class="field-renderer__action-add"
        type="button"
        @click="add(index + 1)"
      />
    </template>
  </component>
</template>

<script>
import { Clone, ExtractString, BracketsRegex } from './utils'
import { path } from 'ramda'
import Templates from './Templates'
import { ButtonComponent } from 'vue-elder-button'
import AutoAnimate from './directives/auto-animate.js'

export default {
  props: {
    value: undefined,
    config: Object,
    schema: Object,
  },
  data() {
    return {
      isCollapsed: true,
      ids: [],
    }
  },
  directives: {
    AutoAnimate,
  },
  inject: ['injection'],
  computed: {
    validator() {
      return this.injection.validator
    },
    validatorPrefix() {
      return this.injection.validatorPrefix
    },
    data() {
      if (this.config.isList) return this.value
      return [this.value]
    },
    style() {
      let style = {
        '--list-item-min-width':
          ExtractString(this.schema.component, BracketsRegex, { group: 2 }) || 'initial',
      }

      let size = path(['options', 'size'], this.schema)
      if (size) style['gridColumnEnd'] = `span ${size}`

      return style
    },
    fieldName() {
      if (this.config.componentName.includes(':')) return 'dynamic'
      return this.config.componentName.toLowerCase()
    },
  },
  methods: {
    getId(index) {
      if (!this.ids[index]) this.ids[index] = crypto.randomUUID()
      return this.ids[index]
    },
    getBinding(index) {
      let base = this.config.binding

      let key = [
        this.schema.parentKey,
        this.schema.parentIndex,
        this.schema.key,
        this.config.isList ? index : null,
      ]
        .filter(e => ![null, undefined].includes(e))
        .join('.')

      let config = {
        ...this.config,
      }

      if (this.config.isList) config.index = index

      let result = {
        config,
        options: this.schema.options,
        label: this.schema.label,
        value: base.value || (this.config.isList ? this.value[index] : this.value),
        ...base,
      }

      if (this.config.isList && !this.config.showLabelCount) {
        delete result.label
        delete result.sublabel
      }

      if (
        this.validator &&
        [this.validatorPrefix, key.replace(/(\.)(\d+)(\.?)/g, '$1*$3')].filter(Boolean).join('.') in
          this.validator.fields
      ) {
        let fullKey = [this.validatorPrefix, key].filter(Boolean).join('.')
        let hasError = fullKey in this.validator.errors.errors
        result['isValid'] = !hasError
        result['validationMessage'] = hasError
          ? this.validator.errors.errors[fullKey].join(' - ')
          : null
      }

      if (this.config.showLabelCount && this.data.length > 1) result.label += ` ${index + 1}`

      return result
    },

    set(val, i) {
      if (this.config.isList) return this.value.splice(i, 1, val)
      this.$emit('input', val)
    },
    add(i) {
      this.$emit('input', [...(this.value || []), this.getTemplate()])
    },
    remove(i) {
      this.ids.splice(i, 1)

      if (this.config.isList) {
        if (this.value.length > 1)
          return this.$emit(
            'input',
            this.value.filter((v, ii) => i !== ii),
          )
        if (this.value.length === 1 && this.config.removable)
          return this.$emit('remove', this.schema)
      } else {
        if (!this.config.removable) return
        return this.$emit('remove', this.schema)
      }
    },
    move(index, delta) {
      // both data and ids do the same re-ordering
      ;[this.ids, this.value].forEach(arr => {
        const item = arr.splice(index, 1)[0]
        arr.splice(index + delta, 0, item)
      })

      this.$emit('input', this.value)
    },
    getTemplate() {
      if (this.config.componentName.includes(':')) {
        let components = this.config.componentName.split(',')
        if (components.length === 1) return undefined
        return Object.fromEntries(components.map(e => [e.split(':')[1], null]))
      }
      return Templates[this.config.componentName]
        ? Clone(Templates[this.config.componentName])
        : undefined
    },
  },
  mounted() {
    if (this.config.isList && (!this.value || !this.value.length)) this.add()
  },
  components: {
    ButtonComponent,
  },
}
</script>

<style lang="scss">
.field-renderer {
  &--list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(var(--list-item-min-width), 1fr));

    grid-gap: 1rem;
    grid-column-end: span 2;

    border: none;
    border-radius: var(--border-radius, 4px);
    padding: 1rem;
    background-color: #f7f7f7;
    margin: 0;

    align-self: self-start;
  }

  &__actions {
    display: flex;
    justify-content: end;
    gap: 0.5rem;
  }

  &__list {
    &-labels {
      padding: 0;

      &-label {
        font-weight: bold;
      }

      &-sublabel {
        font-size: 0.9em;
        opacity: 0.5;
      }
    }

    &-header {
      display: flex;
      align-items: center;
      align-self: flex-start;
      gap: 1rem;
      grid-column: 1/-1;
      cursor: pointer;
      text-align: left;

      &:focus-visible {
        outline: 2px solid currentColor !important;
      }
    }
  }

  &__item {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__action-add {
    grid-column: 1/-1;
    margin: 0 -1rem -1rem -1rem;
    background-color: rgba(black, 0.05) !important;
    border: none !important;
    justify-content: center !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;

    .elder-button__label {
      flex-grow: 0;
    }
  }

  .custom-field {
    flex-grow: 1;
  }
}
</style>
